import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { useCustomAuth } from "@/contexts/CustomAuthContext";
import { authApi } from '@/services/authService';
import { Header } from '@/components/layout/header/Header';
import {
  Search, MousePointerClick, User, Send, X, Info, Plus, Trash2, Pencil, List
} from "lucide-react";
import { toast } from "sonner";

interface IncrementClick {
  _id: string;
  id_user: string;
  email: string;
  clicks: number;
  createdAt?: string;
  updatedAt?: string;
}

export default function IncrementClicks() {
  useLanguage();
  const { user } = useCustomAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.nivel !== 'admin') {
      toast.error(translateInterfaceText("Área restrita a administradores."));
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const [searchEmail, setSearchEmail] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [foundRecord, setFoundRecord] = useState<IncrementClick | null>(null);
  const [searchNotFound, setSearchNotFound] = useState(false);
  const [searchUserId, setSearchUserId] = useState('');

  const [allRecords, setAllRecords] = useState<IncrementClick[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  const [editClicks, setEditClicks] = useState<number>(0);
  const [submittingEdit, setSubmittingEdit] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newClicks, setNewClicks] = useState<number>(0);
  const [submittingCreate, setSubmittingCreate] = useState(false);

  const [editingRecord, setEditingRecord] = useState<IncrementClick | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editListClicks, setEditListClicks] = useState<number>(0);
  const [submittingListEdit, setSubmittingListEdit] = useState(false);

  const fetchAllRecords = async () => {
    try {
      setLoadingList(true);
      const response = await authApi.get('/increment-clicks');
      if (response.data.success) {
        setAllRecords(response.data.data || []);
      }
    } catch (err: any) {
      console.error("Erro ao listar registros:", err);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchAllRecords();
  }, []);

  const handleSearchUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchEmail.trim()) {
      toast.error(translateInterfaceText("Por favor, digite um e-mail."));
      return;
    }
    try {
      setModalLoading(true);
      setFoundRecord(null);
      setSearchNotFound(false);
      setSearchUserId('');
      const response = await authApi.get(`/increment-clicks/${encodeURIComponent(searchEmail)}`);
      if (response.data.success && response.data.data) {
        setFoundRecord(response.data.data);
        setEditClicks(response.data.data.clicks);
        setIsModalOpen(false);
        setSearchEmail('');
        toast.success(translateInterfaceText("Registro encontrado!"));
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setSearchNotFound(true);
        setSearchEmail('');
        setIsModalOpen(false);
        toast.info(translateInterfaceText("Nenhum registro encontrado para este e-mail. Você pode criar um novo."));
      } else {
        toast.error(err.response?.data?.message || translateInterfaceText("Erro ao buscar registro."));
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateClicks = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!foundRecord) return;
    try {
      setSubmittingEdit(true);
      await authApi.patch(`/increment-clicks/${encodeURIComponent(foundRecord.email)}`, { clicks: Number(editClicks) });
      toast.success(translateInterfaceText("Clicks atualizados com sucesso!"));
      setFoundRecord(prev => prev ? { ...prev, clicks: Number(editClicks) } : null);
      fetchAllRecords();
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao atualizar clicks."));
    } finally {
      setSubmittingEdit(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      toast.error(translateInterfaceText("Preencha o e-mail."));
      return;
    }
    try {
      setSubmittingCreate(true);
      const response = await authApi.post('/increment-clicks', {
        email: newEmail.trim(),
        clicks: Number(newClicks),
      });
      toast.success(translateInterfaceText("Registro criado com sucesso!"));
      setIsCreateModalOpen(false);
      setNewEmail('');
      setNewClicks(0);
      fetchAllRecords();
    } catch (err: any) {
      if (err.response?.status === 409) {
        toast.error(translateInterfaceText("Este e-mail já possui um registro de incremento."));
      } else {
        toast.error(err.response?.data?.message || translateInterfaceText("Erro ao criar registro."));
      }
    } finally {
      setSubmittingCreate(false);
    }
  };

  const handleDelete = async (email: string) => {
    if (!confirm(translateInterfaceText("Tem certeza que deseja excluir o registro de {value0}?", { value0: (email) }))) return;
    try {
      await authApi.delete(`/increment-clicks/${encodeURIComponent(email)}`);
      toast.success(translateInterfaceText("Registro excluído com sucesso!"));
      if (foundRecord?.email === email) {
        setFoundRecord(null);
        setEditClicks(0);
      }
      fetchAllRecords();
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao excluir registro."));
    }
  };

  const handleOpenEditModal = (record: IncrementClick) => {
    setEditingRecord(record);
    setEditListClicks(record.clicks);
    setIsEditModalOpen(true);
  };

  const handleListEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRecord) return;
    try {
      setSubmittingListEdit(true);
      await authApi.patch(`/increment-clicks/${encodeURIComponent(editingRecord.email)}`, { clicks: Number(editListClicks) });
      toast.success(translateInterfaceText("Clicks atualizados com sucesso!"));
      setIsEditModalOpen(false);
      setEditingRecord(null);
      fetchAllRecords();
      if (foundRecord?.email === editingRecord.email) {
        setFoundRecord(prev => prev ? { ...prev, clicks: Number(editListClicks) } : null);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao atualizar clicks."));
    } finally {
      setSubmittingListEdit(false);
    }
  };

  if (!user || user.nivel !== 'admin') return null;

  return (
    <Layout>
      <Header title={translateInterfaceText("Increment Clicks")} />
      <div className="p-8 max-w-4xl mx-auto space-y-8 font-sans antialiased text-slate-800 pb-28">

        {/* BANNER PRINCIPAL */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <div>
            <h2 className="text-md font-bold text-slate-900">{translateInterfaceText("Gerenciar Incremento de Clicks")}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{translateInterfaceText("Busque, crie, edite ou exclua registros de incremento condicional de clicks por usuário.")}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-95 shrink-0"
            >
              <Plus size={15} /> {" "}{translateInterfaceText("Novo Registro")}</button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-95 shrink-0"
            >
              <Search size={15} /> {" "}{translateInterfaceText("Buscar por E-mail")}</button>
          </div>
        </div>

        {/* MODAL DE BUSCA POR E-MAIL */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm top-[-5%]">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-xl overflow-hidden max-w-[600px]">
              <div className="p-5 border-b border-slate-50 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Search size={16} className="text-blue-600" />
                  <h3 className="text-sm font-bold text-slate-900">{translateInterfaceText("Buscar Registro por E-mail")}</h3>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg transition-colors">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleSearchUser} className="p-5 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("E-mail do Usuário")}</label>
                  <input
                    type="email"
                    required
                    placeholder={translateInterfaceText("Digite o e-mail completo")}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 outline-none focus:bg-white focus:border-blue-500 transition-all"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50">
                    {translateInterfaceText("Cancelar")}</button>
                  <button type="submit" disabled={modalLoading} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99]">
                    {modalLoading ? translateInterfaceText("Buscando...") : translateInterfaceText("Buscar")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL DE CRIAÇÃO */}
        {isCreateModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm top-[-5%]">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-xl overflow-hidden max-w-[600px]">
              <div className="p-5 border-b border-slate-50 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Plus size={16} className="text-emerald-600" />
                  <h3 className="text-sm font-bold text-slate-900">{translateInterfaceText("Novo Registro de Incremento")}</h3>
                </div>
                <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg transition-colors">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleCreate} className="p-5 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("E-mail do Usuário")}</label>
                  <input
                    type="email"
                    required
                    placeholder={translateInterfaceText("E-mail vinculado ao registro")}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 outline-none focus:bg-white focus:border-blue-500 transition-all"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Clicks a Incrementar")}</label>
                  <input
                    type="number"
                    min="0"
                    required
                    placeholder={translateInterfaceText("Quantidade de clicks")}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 outline-none focus:bg-white focus:border-blue-500 transition-all"
                    value={newClicks}
                    onChange={(e) => setNewClicks(Number(e.target.value))}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setIsCreateModalOpen(false)} className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50">
                    {translateInterfaceText("Cancelar")}</button>
                  <button type="submit" disabled={submittingCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99]">
                    {submittingCreate ? translateInterfaceText("Criando...") : translateInterfaceText("Criar Registro")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL DE EDIÇÃO (LISTA) */}
        {isEditModalOpen && editingRecord && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm top-[-5%]">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-xl overflow-hidden max-w-[600px]">
              <div className="p-5 border-b border-slate-50 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Pencil size={16} className="text-blue-600" />
                  <h3 className="text-sm font-bold text-slate-900">{translateInterfaceText("Editar Clicks —")}{" "}{editingRecord.email}</h3>
                </div>
                <button onClick={() => { setIsEditModalOpen(false); setEditingRecord(null); }} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg transition-colors">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleListEdit} className="p-5 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Clicks")}</label>
                  <input
                    type="number"
                    min="0"
                    required
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 outline-none focus:bg-white focus:border-blue-500 transition-all"
                    value={editListClicks}
                    onChange={(e) => setEditListClicks(Number(e.target.value))}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => { setIsEditModalOpen(false); setEditingRecord(null); }} className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50">
                    {translateInterfaceText("Cancelar")}</button>
                  <button type="submit" disabled={submittingListEdit} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99]">
                    {submittingListEdit ? translateInterfaceText("Salvando...") : translateInterfaceText("Salvar")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* REGISTRO ENCONTRADO PELA BUSCA */}
        {foundRecord && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-50">
                <User size={18} className="text-blue-600" />
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">{translateInterfaceText("Registro Encontrado")}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-xs">
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("ID Usuário:")}</p>
                  <p className="font-mono text-slate-500 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded text-[11px] w-max mt-0.5 select-all">{foundRecord.id_user}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("E-mail:")}</p>
                  <p className="font-medium text-slate-700 mt-0.5">{foundRecord.email}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("Clicks Configurados:")}</p>
                  <p className="font-bold text-amber-600 mt-0.5">{foundRecord.clicks}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("Criado em:")}</p>
                  <p className="font-medium text-slate-700 mt-0.5">{foundRecord.createdAt ? new Date(foundRecord.createdAt).toLocaleString(getInterfaceLanguage()) : '-'}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between space-y-4">
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <MousePointerClick size={14} className="text-amber-500" />
                  <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Editar Clicks")}</span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">{translateInterfaceText("Altere a quantidade de clicks de incremento para este usuário.")}</p>
              </div>
              <form onSubmit={handleUpdateClicks} className="space-y-3">
                <input
                  type="number"
                  min="0"
                  required
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:bg-white transition-all"
                  value={editClicks}
                  onChange={(e) => setEditClicks(Number(e.target.value))}
                />
                <button type="submit" disabled={submittingEdit} className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99]">
                  {submittingEdit ? translateInterfaceText("Salvando...") : translateInterfaceText("Atualizar Clicks")} <Send size={12} />
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ESTADO INICIAL — NENHUM REGISTRO BUSCADO */}
        {!foundRecord && !searchNotFound && allRecords.length === 0 && (
          <div className="bg-white border border-slate-100 rounded-2xl p-12 text-center shadow-sm max-w-xl mx-auto space-y-3">
            <Info size={20} className="text-slate-400 mx-auto" />
            <h3 className="text-sm font-bold text-slate-800">{translateInterfaceText("Nenhum registro selecionado")}</h3>
            <p className="text-xs text-slate-400 max-w-xs mx-auto">{translateInterfaceText("Clique em \"Buscar por E-mail\" para localizar um registro existente, ou \"Novo Registro\" para criar um novo incremento.")}</p>
          </div>
        )}

        {/* NÃO ENCONTRADO — OPÇÃO DE CRIAR */}
        {searchNotFound && (
          <div className="bg-white border border-slate-100 rounded-2xl p-12 text-center shadow-sm max-w-xl mx-auto space-y-3">
            <Info size={20} className="text-amber-500 mx-auto" />
            <h3 className="text-sm font-bold text-slate-800">{translateInterfaceText("Nenhum registro encontrado")}</h3>
            <p className="text-xs text-slate-400 max-w-xs mx-auto">{translateInterfaceText("Nenhum incremento de clicks foi encontrado para o e-mail informado. Você pode criar um novo registro clicando no botão abaixo.")}</p>
            <button
              onClick={() => { setSearchNotFound(false); setIsCreateModalOpen(true); }}
              className="mt-2 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-95 mx-auto"
            >
              <Plus size={15} /> {" "}{translateInterfaceText("Criar Registro")}</button>
          </div>
        )}

        {/* LISTA DE TODOS OS REGISTROS */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-slate-50">
            <List size={18} className="text-slate-500" />
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">{translateInterfaceText("Todos os Registros")}</h3>
            <span className="text-[11px] text-slate-400 ml-auto">{allRecords.length} {" "}{translateInterfaceText("registro(s)")}</span>
          </div>
          {loadingList ? (
            <p className="text-xs text-slate-400 text-center py-6">{translateInterfaceText("Carregando registros...")}</p>
          ) : allRecords.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">{translateInterfaceText("Nenhum registro de incremento cadastrado.")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase font-semibold text-slate-400 tracking-wider border-b border-slate-100">
                    <th className="pb-2 pr-4">{translateInterfaceText("E-mail")}</th>
                    <th className="pb-2 pr-4">{translateInterfaceText("ID Usuário")}</th>
                    <th className="pb-2 pr-4 text-right">{translateInterfaceText("Clicks")}</th>
                    <th className="pb-2 pr-4">{translateInterfaceText("Criado em")}</th>
                    <th className="pb-2 text-right">{translateInterfaceText("Ações")}</th>
                  </tr>
                </thead>
                <tbody>
                  {allRecords.map((record) => (
                    <tr key={record._id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="py-2.5 pr-4 font-medium text-slate-700">{record.email}</td>
                      <td className="py-2.5 pr-4 font-mono text-slate-500 text-[11px]">{record.id_user}</td>
                      <td className="py-2.5 pr-4 text-right font-bold text-amber-600">{record.clicks}</td>
                      <td className="py-2.5 pr-4 text-slate-500">{record.createdAt ? new Date(record.createdAt).toLocaleDateString(getInterfaceLanguage()) : '-'}</td>
                      <td className="py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleOpenEditModal(record)}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                            title={translateInterfaceText("Editar clicks")}
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => handleDelete(record.email)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                            title={translateInterfaceText("Excluir registro")}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
